import { Field } from './EventFormPrimitives'
import type { RsvpCloseOption } from '../../lib/rsvpClose'

// RSVP settings (superset of the create + edit field sets): max guests, RSVPs-close,
// and the approval gate.
//
// For a PRIVATE HOME the two underlying flags (require-approval + hide-address) move
// together as a single control — you can't "reveal on approval" without an approval
// step, and showing your exact address to anyone who opens the event contradicts
// marking it private. So one toggle drives both flags at once.
//
// For a VENUE / PIN the address is public either way, so only the approval gate (guest
// vetting / capacity) is meaningful; hide-address is forced off and never shown.
// LocationPicker already clears hide-address when switching to those modes.
//
// Visibility is rendered separately (shared VisibilityPicker) by each page.

export function RsvpSettingsFields({
  maxAttendees,
  onMaxAttendeesChange,
  rsvpClose,
  onRsvpCloseChange,
  requiresApproval,
  onRequiresApprovalChange,
  onHideAddressChange,
  locationMode,
}: {
  maxAttendees: string
  onMaxAttendeesChange: (value: string) => void
  rsvpClose: RsvpCloseOption
  onRsvpCloseChange: (value: RsvpCloseOption) => void
  requiresApproval: boolean
  onRequiresApprovalChange: (value: boolean) => void
  onHideAddressChange: (value: boolean) => void
  locationMode: 'private' | 'venue' | 'pin'
}) {
  const isPrivateHome = locationMode === 'private'
  const homeWarn = isPrivateHome && !requiresApproval

  // Private home: approval + address-hiding are one decision, so set both together.
  // Venue/pin: only the approval gate matters; the address stays public (hide = false).
  function handleToggle(checked: boolean) {
    onRequiresApprovalChange(checked)
    onHideAddressChange(isPrivateHome ? checked : false)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid sm:grid-cols-2 gap-3.5">
        <Field label="Max guests" htmlFor="maxAttendees">
          <input
            id="maxAttendees"
            type="number"
            min={1}
            value={maxAttendees}
            onChange={(e) => onMaxAttendeesChange(e.target.value)}
            placeholder="No limit"
            className="input"
          />
          <div className="text-[11.5px] text-muted">Leave blank for no limit.</div>
        </Field>
        <Field label="RSVPs close" htmlFor="rsvpClose">
          <select
            id="rsvpClose"
            value={rsvpClose}
            onChange={(e) => onRsvpCloseChange(e.target.value as RsvpCloseOption)}
            className="input"
          >
            <option value="none">Don't close early</option>
            <option value="1h">1 hour before</option>
            <option value="24h">1 day before</option>
          </select>
        </Field>
      </div>

      <div className="flex items-start gap-3 p-3.5 rounded-xl bg-s2 border border-line-soft">
        <input
          type="checkbox"
          id="rsvpApproval"
          checked={requiresApproval}
          onChange={(e) => handleToggle(e.target.checked)}
          aria-label={isPrivateHome ? 'Require approval to share your location' : 'Require RSVP approval'}
          className="w-[18px] h-[18px] cursor-pointer accent-primary mt-0.5 shrink-0"
        />
        <div className="flex-1">
          <label htmlFor="rsvpApproval" className="text-[13.5px] font-semibold cursor-pointer select-none">
            {isPrivateHome ? 'Require approval to share your location' : 'Require RSVP approval'}
            {isPrivateHome && (
              <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary-soft text-primary-deep align-middle">
                Recommended
              </span>
            )}
          </label>
          <div className="text-[12px] text-muted mt-0.5">
            {isPrivateHome
              ? 'Guests see only the general area until you approve their RSVP — then they get the exact location.'
              : 'Approve each guest before they’re confirmed. Your location is public either way.'}
          </div>
        </div>
      </div>

      {homeWarn && (
        <div
          className="px-4 py-3 rounded-xl text-[13px]"
          style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#b45309' }}
        >
          You picked a private home — we recommend keeping this on so guests only get your exact location after you approve them.
        </div>
      )}
    </div>
  )
}
